"use strict";
// Verbal rating 1..9 of a z-value: classic stanine bands (Gerald, decision
// 19.10.2026). Only the wording ("Gute Leistung", …) uses these bands; the
// GPfit points stay 2·z + 5 (CalculationsService.calculateSTA9) and the
// Gesundheits-/Fitnesspotential sums are unchanged. See
// documentation/charts.md "Rating 1–9 (stanine bands)".
Object.defineProperty(exports, "__esModule", { value: true });
exports.STANINE_RANGE_WORDS_DE = exports.STANINE_EPSILON = exports.STANINE_Z_EDGES = void 0;
exports.stanineBand = stanineBand;
exports.stanineZRange = stanineZRange;
exports.stanineRangeText = stanineRangeText;
exports.roundRatingZ = roundRatingZ;
exports.ratingPoints = ratingPoints;
/**
 * Lower edges of the bands 2..9 in z. Band k (2..8) is
 * [STANINE_Z_EDGES[k − 2], STANINE_Z_EDGES[k − 1]); band 1 is everything
 * below −1,75 and band 9 everything from +1,75. Lower edge inclusive, upper
 * edge exclusive. Equivalent on the points scale: band = 2·z + 5 rounded half
 * up, clamped 1..9 (edges at 1,5 / 2,5 / … / 8,5 points).
 */
exports.STANINE_Z_EDGES = [
    -1.75, -1.25, -0.75, -0.25, 0.25, 0.75, 1.25, 1.75,
];
/**
 * Tolerance for float noise at an edge. A z that should be exactly on an
 * edge can arrive a few ulp below it, e.g. (value − mean) / sd or
 * (points − 5) / 2 giving 0.24999999999999997 instead of 0.25; it must land
 * in the upper band (6). 1e-12 is far above the noise of |z| < 10 (ulp ≈
 * 2e-15) and far below any real measurement difference, so z = edge − 1e-9
 * still stays in the lower band.
 */
exports.STANINE_EPSILON = 1e-12;
/**
 * Stanine band 1..9 of an UNROUNDED z (do not pass the 1-decimal display
 * value; formatRatingZ is built so that it cannot contradict this anyway):
 * 1: z < −1,75 · 2: −1,75 ≤ z < −1,25 · 3: −1,25 ≤ z < −0,75 ·
 * 4: −0,75 ≤ z < −0,25 · 5: −0,25 ≤ z < +0,25 · 6: +0,25 ≤ z < +0,75 ·
 * 7: +0,75 ≤ z < +1,25 · 8: +1,25 ≤ z < +1,75 · 9: z ≥ +1,75.
 * ±Infinity -> 1 / 9; NaN -> NaN (callers skip missing values first).
 */
function stanineBand(z) {
    if (Number.isNaN(z))
        return NaN;
    let band = 1;
    for (const edge of exports.STANINE_Z_EDGES) {
        if (z >= edge - exports.STANINE_EPSILON)
            band++;
    }
    return band;
}
/** z range of a band for legends: [from, to), null = open end. */
function stanineZRange(band) {
    return {
        from: band <= 1 ? null : exports.STANINE_Z_EDGES[band - 2],
        to: band >= 9 ? null : exports.STANINE_Z_EDGES[band - 1],
    };
}
exports.STANINE_RANGE_WORDS_DE = {
    below: "unter",
    between: "bis unter",
    from: "ab",
};
/**
 * Range of a band as legend text, e.g. "−0,25 bis unter +0,25" (band 5),
 * "unter −1,75" (1), "ab +1,75" (9). `format` writes one edge (z or points).
 */
function stanineRangeText(band, format, words = exports.STANINE_RANGE_WORDS_DE) {
    const { from, to } = stanineZRange(band);
    if (from === null)
        return `${words.below} ${format(to)}`;
    if (to === null)
        return `${words.from} ${format(from)}`;
    return `${format(from)} ${words.between} ${format(to)}`;
}
/**
 * Round `value` to `decimals` so that the shown number never falls in another
 * stanine band than the unrounded value: half up (toward +∞) to the nearest
 * step; if that lands in a higher band, one step lower.
 * - 1 decimal: never needs the step back. The band edges (±0,25, ±0,75, …)
 *   are exactly the rounding midpoints and both rules put the midpoint into
 *   the upper interval: −0,25 -> −0,2 (5), +0,25 -> +0,3 (6), −0,26 -> −0,3.
 * - 2+ decimals: the edges are values of the grid, so only values just below
 *   an edge step back: +0,249 -> +0,24 (not +0,25), −0,251 -> −0,26 (not
 *   −0,25); everything else is plain rounding (−2,6314 -> −2,63).
 * - 0 decimals is not supported (an edge would be inside a rounding step).
 * The same STANINE_EPSILON is applied, so an edge with float noise shows
 * the edge's band. The error is at most half a step (1 decimal) / 1 step.
 */
function roundRatingZ(value, decimals) {
    if (decimals < 1)
        throw new RangeError("roundRatingZ needs decimals >= 1");
    const f = 10 ** decimals;
    let steps = Math.floor(value * f + exports.STANINE_EPSILON * f + 0.5);
    if (stanineBand(steps / f) > stanineBand(value))
        steps -= 1;
    // Normalise −0 (e.g. −0,04 -> 0, shown without sign).
    return steps / f + 0;
}
/**
 * GPfit points of a z for a 1-decimal label without crossing a stanine band:
 * points = 2·z + 5 clamped 1..9, rounded half up; if that lands in a higher
 * band, one step lower (the band edges 1,5 … 8,5 are values of the grid).
 * E.g. z +0,24 -> 5,48 -> "5,4" (5, not "5,5"), z +0,25 -> "5,5" (6),
 * z −0,25 -> "4,5" (5), z +0,99 -> 6,98 -> "7,0" (7).
 */
function ratingPoints(z) {
    const points = Math.max(1, Math.min(9, 2 * z + 5));
    let steps = Math.floor(points * 10 + 2 * exports.STANINE_EPSILON * 10 + 0.5);
    if (stanineBand((steps / 10 - 5) / 2) > stanineBand(z))
        steps -= 1;
    return steps / 10 + 0;
}
